import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { cjCollection } from "../../lib/cj";
import { useSeo } from "../../hooks/useSeo";
import { MarketCard } from "../../components/market/MarketCard";
import { resolveCollectionHandle, COMING_SOON } from "../../config/taxonomy";

const SORTS = [
  { v: "popular", l: "Most Popular" },
  { v: "newest", l: "Newest" },
  { v: "price_low", l: "Price: Low to High" },
  { v: "price_high", l: "Price: High to Low" },
];

const useFilteredGrid = (fetcher) => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({});
  const sentinel = useRef(null);

  const load = useCallback(async (p, reset) => {
    setLoading(true);
    try {
      const d = await fetcher(p);
      setItems((prev) => (reset ? d.products : [...prev, ...d.products]));
      setPages(d.pages || 1); setCount(d.count || 0); setMeta({ title: d.title, description: d.description });
    } finally { setLoading(false); }
  }, [fetcher]);

  useEffect(() => { setItems([]); setPage(1); load(1, true); }, [load]);

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver((e) => {
      if (e[0].isIntersecting && !loading && page < pages) {
        const next = page + 1; setPage(next); load(next, false);
      }
    }, { rootMargin: "400px" });
    io.observe(el);
    return () => io.disconnect();
  }, [page, pages, loading, load]);

  return { items, count, loading, meta, sentinel, page, pages };
};

const MarketCollection = () => {
  const { handle } = useParams();
  const canonical = resolveCollectionHandle(handle);
  // Reversible alias -> canonical redirect (backward-compatible URLs, no broken links).
  if (canonical !== handle) return <Navigate to={`/market/collection/${canonical}`} replace />;
  return <MarketCollectionView handle={handle} />;
};

const MarketCollectionView = ({ handle }) => {
  const [sort, setSort] = useState("popular");
  const [minP, setMinP] = useState("");
  const [maxP, setMaxP] = useState("");
  const [applied, setApplied] = useState({ sort: "popular", min: "", max: "" });

  const fetcher = useCallback((p) => cjCollection(handle, {
    page: p, size: 24, sort: applied.sort,
    min_price: applied.min || undefined, max_price: applied.max || undefined,
  }), [handle, applied]);

  const { items, count, loading, meta, sentinel } = useFilteredGrid(fetcher);
  const apply = () => setApplied({ sort, min: minP, max: maxP });

  useSeo({
    title: meta.title ? `${meta.title} | TAVEN Marketplace` : "Shop | TAVEN Marketplace",
    description: meta.description || `Shop ${meta.title || "products"} on TAVEN with fast US shipping.`,
    jsonLd: {
      "@context": "https://schema.org", "@type": "CollectionPage",
      name: meta.title, description: meta.description,
      mainEntity: { "@type": "ItemList", numberOfItems: count,
        itemListElement: items.slice(0, 20).map((p, i) => ({
          "@type": "ListItem", position: i + 1, name: p.title,
          url: `${window.location.origin}/market/product/${p.handle}` })) },
    },
  });

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="market-collection">
      <h1 className="text-2xl md:text-4xl font-black font-display tracking-tighter capitalize">{meta.title || handle}</h1>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mt-5 mb-6 p-4 bg-taven-gray rounded-xl" data-testid="market-filters">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Sort</label>
          <select data-testid="market-sort" value={sort} onChange={(e) => setSort(e.target.value)}
            className="h-9 px-3 rounded-md border border-black/10 bg-white text-sm">
            {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Min $</label>
          <input data-testid="market-min-price" type="number" value={minP} onChange={(e) => setMinP(e.target.value)}
            className="h-9 w-24 px-3 rounded-md border border-black/10 bg-white text-sm" placeholder="0" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Max $</label>
          <input data-testid="market-max-price" type="number" value={maxP} onChange={(e) => setMaxP(e.target.value)}
            className="h-9 w-24 px-3 rounded-md border border-black/10 bg-white text-sm" placeholder="999" />
        </div>
        <button data-testid="market-apply-filters" onClick={apply}
          className="h-9 px-5 rounded-md bg-black text-white text-xs font-bold uppercase tracking-wider hover:bg-taven-gold hover:text-black transition-colors">Apply</button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {items.map((p) => <MarketCard key={p.taven_product_id || p.handle} product={p} />)}
      </div>
      {items.length === 0 && !loading && (
        COMING_SOON.has(handle) || count === 0 ? (
          <div className="py-16 text-center" data-testid="collection-coming-soon">
            <p className="text-lg font-black font-display tracking-tight">Coming Soon</p>
            <p className="text-sm text-muted-foreground mt-1">New arrivals for this collection are landing shortly.</p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-12 text-center">No products match your filters.</p>
        )
      )}
      {loading && <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>}
      <div ref={sentinel} className="h-4" />
    </div>
  );
};

export default MarketCollection;
